import {
  Company_Read,
  IamRole_Read as UserRoleProto,
  NotificationMethod,
  StytchMemberStatus,
  TimeZone,
  User_Create_In_Company,
  User_Read,
  User_Update,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { UserValidationDTOWithSitesDiff } from '~components/UserForm/UserForm';
import { UserNotificationsPreferenceDTO } from '~components/UserForm/UserNotificationsPreferenceForm';
import { AdminCompany } from '~hooks/admin/useCompaniesAdmin/models';
import { BasicEquipment } from '~hooks/useEquipment/models';
import { BasicLabel } from '~hooks/useLabels/models';
import {
  getSelectedNotificationMethodsByNotification,
  RoleItem,
  UserPreferences,
} from '~hooks/useUsers/models';
import { Nullable } from '~types/Nullable';

export class AdminUser {
  public static parse(proto: User_Read) {
    return new AdminUser(
      proto.id ?? '',
      proto?.first_name ?? '',
      proto.last_name ?? '',
      proto?.company ? AdminCompany.parse(proto?.company as Company_Read) : null,
      proto?.iam_roles?.length
        ? proto?.iam_roles.map((userSingleRole) => {
            return RoleItem.parse(userSingleRole as UserRoleProto);
          })
        : [],
      proto.email,
      proto.phone,
      proto?.stytch_member_id ?? null,
      proto?.stytch_member_status ?? undefined,
      proto?.stytch_user_id ?? null,
      proto?.stytch_user_status ?? undefined,
      proto?.created_at ?? '',
      proto?.updated_at ?? '',
      proto?.time_zone ?? '',
      proto?.preferences ? UserPreferences.parse(proto.preferences) : null,
      proto?.text_to_accept_enabled,
      proto?.external_id,
      proto?.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto?.last_login_at ? dayjs(proto.last_login_at) : null,
      proto?.labels?.map((label) => BasicLabel.parse(label)) ?? [],
      proto?.accounting_id ?? null,
      proto?.hide_rates ?? false,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  private static deparseFields(
    proto: AdminUser &
      Pick<
        UserValidationDTOWithSitesDiff,
        | 'textToAcceptFieldEnabled'
        | 'textToAcceptFieldValue'
        | 'noLocationAlertFieldEnabled'
        | 'noLocationAlertFieldValue'
        | 'hideRatesFieldEnabled'
        | 'hideRatesFieldValue'
      >,
  ) {
    const data: Partial<User_Update | User_Create_In_Company> = {
      iam_role_ids: proto.userRoles?.map((role: RoleItem) => role.id) ?? [],
      first_name: proto.firstName,
      last_name: proto.lastName,
      ...(proto.email ? { email: proto.email } : {}),
      ...(proto.phone ? { phone: proto.phone.replace(/\s/g, '') } : {}),
      time_zone: proto.timeZone as TimeZone,
      external_id: proto.externalId,
      accounting_id: proto.accountingId,
      equipment_id: proto.equipment?.id ?? null,
      label_ids: proto.labels?.map((label: BasicLabel) => label.id) ?? [],
    };

    if (!isNil(proto.textToAcceptFieldEnabled)) {
      data.text_to_accept_enabled = Boolean(proto.textToAcceptFieldValue);
    }

    // hideRatesFieldEnabled is only true when the viewer holds edit_user_hide_rate, so
    // the field must be omitted from the payload entirely otherwise — never sent as false
    if (proto.hideRatesFieldEnabled) {
      data.hide_rates = Boolean(proto.hideRatesFieldValue);
    }

    return data;
  }

  public static deparse(
    proto: AdminUser &
      Pick<
        UserValidationDTOWithSitesDiff,
        | 'textToAcceptFieldEnabled'
        | 'textToAcceptFieldValue'
        | 'noLocationAlertFieldEnabled'
        | 'noLocationAlertFieldValue'
        | 'hideRatesFieldEnabled'
        | 'hideRatesFieldValue'
      >,
    invitesToSend?: User_Create_In_Company['send_invite'],
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
  ): User_Update | User_Create_In_Company {
    const data = AdminUser.deparseFields(proto);

    if (proto.id) {
      const updateUserData = data as User_Update;

      if (userNotificationsPreferenceData) {
        updateUserData.preferences = {
          notifications: getSelectedNotificationMethodsByNotification(
            userNotificationsPreferenceData,
          ),
        };
      }

      if (!isNil(proto.noLocationAlertFieldEnabled)) {
        if (!updateUserData.preferences) {
          updateUserData.preferences = { notifications: {} };
        }
        updateUserData.preferences.notifications = {
          ...updateUserData.preferences.notifications,
          no_location_alert: proto.noLocationAlertFieldValue
            ? [NotificationMethod.PUSH]
            : [],
        };
      }

      return updateUserData;
    }

    return AdminUser.toCompanyCreateData(data, invitesToSend);
  }

  // The company comes from the company-scoped create endpoint's path; that schema rejects a body company_id
  public static deparseForCompanyCreate(
    proto: Parameters<typeof AdminUser.deparseFields>[0],
    invitesToSend?: User_Create_In_Company['send_invite'],
  ) {
    return AdminUser.toCompanyCreateData(AdminUser.deparseFields(proto), invitesToSend);
  }

  private static toCompanyCreateData(
    data: Partial<User_Update | User_Create_In_Company>,
    invitesToSend?: User_Create_In_Company['send_invite'],
  ) {
    const createUserData = data as User_Create_In_Company;
    createUserData.send_invite = invitesToSend;

    return createUserData;
  }

  public get id(): string {
    return this._id;
  }

  public get firstName(): string {
    return this._firstName;
  }

  public get lastName(): string {
    return this._lastName;
  }

  public get company(): Nullable<AdminCompany> {
    return this._company;
  }

  public get userRoles() {
    return this._userRoles;
  }

  public get email(): Nullable<string> {
    return this._email;
  }

  public get phone(): Nullable<string> {
    return this._phone;
  }

  public get createdAt(): string {
    return this._createdAt;
  }

  public get updatedAt(): string {
    return this._updatedAt;
  }

  public get status(): StytchMemberStatus {
    return this._stytch_member_status;
  }
  public get isActiveMember(): boolean {
    return this._stytch_member_status === StytchMemberStatus.ACTIVE;
  }

  public get isActiveUser(): boolean {
    return this._stytch_user_status === StytchMemberStatus.ACTIVE;
  }

  public get timeZone(): string {
    return this._time_zone;
  }

  public get preferences() {
    return this._preferences;
  }

  public get textToAcceptEnabled() {
    return this._text_to_accept_enabled;
  }

  public get externalId(): string | null {
    return this._external_id;
  }

  public get accountingId(): string | null {
    return this._accounting_id;
  }

  public get equipment(): Nullable<BasicEquipment> {
    return this._equipment;
  }

  public get lastLoginAt() {
    return this._last_login_at;
  }

  public get labels() {
    return this._labels;
  }

  public get hideRates() {
    return this._hide_rates;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _firstName: string,
    private _lastName: string,
    private _company: Nullable<AdminCompany>,
    private _userRoles: Nullable<RoleItem[]>,
    private _email: Nullable<string>,
    private _phone: Nullable<string>,
    private _stytch_member_id: string | null,
    private _stytch_member_status: StytchMemberStatus,
    private _stytch_user_id: string | null,
    private _stytch_user_status: StytchMemberStatus,
    private _createdAt: string,
    private _updatedAt: string,
    private _time_zone: string,
    private _preferences: Nullable<UserPreferences>,
    private _text_to_accept_enabled: boolean,
    private _external_id: string | null,
    private _equipment: Nullable<BasicEquipment>,
    private _last_login_at: Dayjs | null,
    private _labels: BasicLabel[],
    private _accounting_id: string | null = null,
    private _hide_rates: boolean = false,
    private _discarded_at: Dayjs | null = null,
  ) {}
}
